/**
 * RailStatus — bridge wave-2 item 5: the left rail's top block. Absorbs
 * NowCard's facts into the density format of plan §1's STATUS block:
 *
 *   BUILDING                    ← mood word (MOOD_STYLE[mood].label)
 *   run_8f3a9c2d…               ← truncated to 12 chars, full id in title
 *   PH 3/6 · GATE               ← current_phase/total_phases + stage label
 *   items 2/7 · journal 4s      ← terminal items / total · LIVE journal age
 *   pr 2 · commit 3             ← per-item PR/commit line count (items_summary)
 *
 * CONSUMERS:
 * - frontend/src/pages/FleetHud.tsx: bridge left rail, top block (inside a
 *   RailBlock). NowCard stays untouched as the compact/mobile status card —
 *   this component never replaces it outside the bridge path.
 *
 * Truth contract (every field real, from the EXISTING overview payload):
 * - Journal age is LIVE: overview.journal_age_seconds is the age at poll
 *   time; useNow(1000) re-derives `age + (now − polledAt)` every second so
 *   staleness is always visible (never a frozen snapshot string).
 * - items x/y counts a terminal status against ReactorCore's exported
 *   TERMINAL_STATUSES — the same set the core's tick ring lights on, so the
 *   rail and the instrument can never disagree about "done".
 * - Stage label via reactorStageForPhase(current_phase) — the same mapping
 *   BridgeCore's readout uses.
 * - Zero motion: this block is text only, so prefers-reduced-motion needs
 *   no special path.
 *
 * Chrome: squared (no rounding classes at all), DM Mono, house tokens only
 * (text-text-*, text-brand); the only hex in the file is none — the accent
 * arrives via the brand token.
 */
import React from 'react';
import { FleetOverview } from '../../services/fleetHudApi';
import { FleetMood, MOOD_STYLE, useNow } from '../fleet/fleetMood';
import { formatElapsedSeconds } from '../fleet/fleetTime';
import { TERMINAL_STATUSES, reactorStageForPhase } from '../fleet/ReactorCore';

/** Run ids are long; the rail shows ~12 chars and keeps the full id on hover. */
const RUN_ID_DISPLAY_CHARS = 12;

interface RailStatusProps {
  mood: FleetMood;
  overview: FleetOverview | null;
  /** Last successful poll time (ms epoch) — anchors the live journal age. */
  polledAt: number | null;
}

export default function RailStatus({ mood, overview, polledAt }: RailStatusProps) {
  // The honest heartbeat: recomputes now − realTimestamp once a second.
  const now = useNow(1000);

  if (!overview) {
    return (
      <div data-testid="rail-status" className="px-3 py-2 font-mono text-xs text-text-muted">
        CONTACTING FLEET…
      </div>
    );
  }

  const run = overview.latest_run;
  const items = run?.items_summary ?? [];
  const doneCount = items.filter((it) => it.status && TERMINAL_STATUSES.has(it.status)).length;
  const prCount = items.filter((it) => it.pr_url).length;
  const commitCount = items.filter((it) => it.commit).length;

  // Live journal age: poll-time age + elapsed since that poll. Falls back to
  // the raw poll-time value when polledAt is somehow unset — degraded, never
  // fabricated.
  const baseAge = overview.journal_age_seconds;
  const liveAgeSeconds =
    baseAge === null || baseAge === undefined
      ? null
      : polledAt !== null
        ? baseAge + Math.max(0, (now - polledAt) / 1000)
        : baseAge;

  const stageLabel = run ? reactorStageForPhase(run.current_phase) : '';
  const runId = run?.run_id ?? null;
  const runIdDisplay =
    runId && runId.length > RUN_ID_DISPLAY_CHARS
      ? `${runId.slice(0, RUN_ID_DISPLAY_CHARS)}…`
      : (runId ?? '—');

  return (
    <div data-testid="rail-status" className="flex flex-col gap-1 px-3 py-2 font-mono">
      {/* Mood word — the 3-meter read, same label the top-bar pill shows. */}
      <div className="text-[13px] font-semibold tracking-[0.2em] text-text-primary">
        {MOOD_STYLE[mood].label}
      </div>

      {/* Run id — truncated with the full value in the title tooltip; the row
          itself can never wrap or clip (min-w-0 + truncate). */}
      <div className="min-w-0 text-[11px] text-text-secondary">
        <span className="block truncate" title={runId ?? undefined}>
          {runIdDisplay}
        </span>
      </div>

      {/* Phase position + stage label. */}
      <div className="text-[11px] text-text-secondary">
        PH {run?.current_phase ?? '—'}/{run?.total_phases ?? '—'}
        {stageLabel ? <span className="text-text-primary"> · {stageLabel}</span> : null}
      </div>

      {/* Items done/total + live journal age. */}
      <div className="text-[11px] text-text-muted">
        items {run ? doneCount : '—'}/{run ? items.length : '—'} · journal{' '}
        {formatElapsedSeconds(liveAgeSeconds)} ago
      </div>

      {/* Per-item PR/commit line count — NowCard's link rows, densified to
          a single count row (the links themselves live in RecentRuns). */}
      <div className="text-[11px] text-text-muted">
        pr <span className="text-brand">{prCount}</span> · commit{' '}
        <span className="text-brand">{commitCount}</span>
      </div>
    </div>
  );
}
